import { useCallback, useEffect, useState } from "react"

import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet"
import { Skeleton } from "@/components/ui/skeleton"
import type { OsmCandidate } from "@/lib/types"

interface Props {
  searchUrl: string
  onSelect: (candidate: OsmCandidate) => void
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function ImportSheet({ searchUrl, onSelect, open, onOpenChange }: Props) {
  const [query, setQuery] = useState("")
  const [candidates, setCandidates] = useState<OsmCandidate[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const search = useCallback(async (q: string, signal?: AbortSignal) => {
    if (!q.trim()) { setCandidates([]); return }
    setLoading(true)
    setError(null)
    try {
      // Uses raw fetch (not router.get) because this read-only JSON polling endpoint
      // requires AbortController semantics to cancel in-flight requests on rapid input.
      const res = await fetch(`${searchUrl}?q=${encodeURIComponent(q)}`, {
        signal,
        headers: {
          "Accept": "application/json",
          "X-CSRF-Token": document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content ?? "",
        },
      })
      if (signal?.aborted) return
      const data = await res.json()
      if (data.error) {
        setError("Search unavailable — add manually.")
        setCandidates([])
      } else {
        setCandidates(data.candidates ?? [])
      }
    } catch (err) {
      if (err instanceof Error && err.name === "AbortError") return
      setError("Search unavailable — add manually.")
    } finally {
      if (!signal?.aborted) setLoading(false)
    }
  }, [searchUrl])

  // 300ms debounce — cancel in-flight fetch when query changes
  useEffect(() => {
    const controller = new AbortController()
    const timer = setTimeout(() => { search(query, controller.signal) }, 300)
    return () => { clearTimeout(timer); controller.abort() }
  }, [query, search])

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-[480px]">
        <SheetHeader>
          <SheetTitle>Import from OpenStreetMap</SheetTitle>
        </SheetHeader>
        <div className="mt-4 space-y-4">
          <Input
            placeholder="Search OpenStreetMap..."
            value={query}
            onChange={e => setQuery(e.target.value)}
            aria-label="Search OpenStreetMap"
          />
          {error && <p className="text-sm text-destructive">{error}</p>}
          {loading ? (
            <div className="space-y-2">
              {[0, 1, 2].map(i => <Skeleton key={i} className="h-16 w-full rounded-md" />)}
            </div>
          ) : candidates.length === 0 && query.trim() && !loading ? (
            <p className="text-sm text-muted-foreground">No results — try a broader search or add manually.</p>
          ) : (
            <div className="space-y-2">
              {candidates.map((c, i) => (
                <Card key={`${c.osm_id}-${i}`} className="p-3 flex items-center gap-3">
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-sm truncate">{c.name}</p>
                    <p className="text-xs text-muted-foreground line-clamp-2">{c.address}</p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {c.feature_type && <Badge variant="secondary" className="text-xs">{c.feature_type}</Badge>}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => { onSelect(c); onOpenChange(false) }}
                    >
                      Use this
                    </Button>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  )
}
